import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PaletteIcon from '@mui/icons-material/Palette';
import SaveIcon from '@mui/icons-material/Save';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useTheme } from '@mui/material/styles';
import { db, functions, storage } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { workspaceShellBackgroundSx } from '../../lib/workspaceBranding';
import {
  BACKGROUND_ACCEPT,
  BACKGROUND_SPECS,
  base64ToBlob,
  BRANDING_ASSET_TYPES,
  DEFAULT_BACKGROUND_PROMPTS,
  DEFAULT_LOGO_PROMPTS,
  filePreviewUrl,
  isAnimatedBackgroundMime,
  LOGO_ACCEPT,
  LOGO_SPECS,
  resolveBrandingPrompt,
} from '../../lib/brandingAssets';

/**
 * Company-admin workspace branding: logo + dashboard background via upload or AI.
 * @param {{ userData?: { companyId?: string, role?: string } }} props
 */
export default function WorkspaceBrandingSettings({ userData }) {
  const theme = useTheme();
  const { workspace, companyId, refresh } = useWorkspace();
  const resolvedCompanyId = companyId || userData?.companyId;

  const [portalBrandingEnabled, setPortalBrandingEnabled] = useState(false);
  const [brandColor, setBrandColor] = useState('#D85A30');
  const [portalBackgroundKind, setPortalBackgroundKind] = useState('default');
  const [portalBackgroundSolidColor, setPortalBackgroundSolidColor] = useState('#1976d2');

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoPresetId, setLogoPresetId] = useState(DEFAULT_LOGO_PROMPTS[0].id);
  const [logoPrompt, setLogoPrompt] = useState('');
  const [generatingLogo, setGeneratingLogo] = useState(false);

  const [bgFile, setBgFile] = useState(null);
  const [bgPreview, setBgPreview] = useState('');
  const [bgPresetId, setBgPresetId] = useState(DEFAULT_BACKGROUND_PROMPTS[0].id);
  const [bgPrompt, setBgPrompt] = useState('');
  const [generatingBg, setGeneratingBg] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!workspace) return;
    setPortalBrandingEnabled(Boolean(workspace.portalBrandingEnabled));
    setBrandColor(workspace.brandColor || '#D85A30');
    setPortalBackgroundKind(workspace.portalBackgroundKind || 'default');
    setPortalBackgroundSolidColor(
      workspace.portalBackgroundSolidColor || workspace.brandColor || '#1976d2',
    );
    setLogoPreview(workspace.logoUrl || '');
    setBgPreview(workspace.portalBackgroundImageUrl || '');
    setLogoFile(null);
    setBgFile(null);
  }, [workspace]);

  const previewPortal = useMemo(
    () => ({
      portalBrandingEnabled,
      logoUrl: logoPreview || workspace?.logoUrl || '',
      brandColor,
      portalBackgroundKind,
      portalBackgroundSolidColor,
      portalBackgroundImageUrl: bgPreview || workspace?.portalBackgroundImageUrl || '',
      name: workspace?.name || '',
    }),
    [
      portalBrandingEnabled,
      logoPreview,
      workspace,
      brandColor,
      portalBackgroundKind,
      portalBackgroundSolidColor,
      bgPreview,
    ],
  );

  const shellSx = workspaceShellBackgroundSx(theme, previewPortal);

  const generateAsset = async (assetType) => {
    setError('');
    setSuccess('');
    const isLogo = assetType === BRANDING_ASSET_TYPES.LOGO;
    const presetId = isLogo ? logoPresetId : bgPresetId;
    const customPrompt = isLogo ? logoPrompt : bgPrompt;
    const prompt = resolveBrandingPrompt(assetType, presetId, customPrompt, {
      companyName: workspace?.name,
      brandColor,
    });

    if (isLogo) setGeneratingLogo(true);
    else setGeneratingBg(true);

    try {
      const callable = httpsCallable(functions, 'generateBrandingAsset');
      const { data } = await callable({
        assetType,
        companyId: resolvedCompanyId,
        prompt,
        companyName: workspace?.name || '',
        brandColor,
      });

      const mimeType = data?.mimeType || 'image/png';
      const base64 = data?.base64;
      if (!base64) {
        throw new Error('No image returned from AI.');
      }

      const blob = base64ToBlob(base64, mimeType);
      const ext = mimeType.includes('png') ? 'png' : 'webp';
      const file = new File([blob], `ai-${assetType}.${ext}`, { type: mimeType });

      if (isLogo) {
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
      } else {
        setBgFile(file);
        setBgPreview(URL.createObjectURL(file));
        setPortalBackgroundKind('image');
      }
      setSuccess(
        isLogo ?
          'Logo generated — review the preview, then save to apply.' :
          'Background generated — review the preview, then save to apply.',
      );
    } catch (e) {
      console.error('generateBrandingAsset:', e);
      setError(e.message || 'Could not generate image with AI.');
    } finally {
      if (isLogo) setGeneratingLogo(false);
      else setGeneratingBg(false);
    }
  };

  const handleLogoUpload = (file) => {
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(filePreviewUrl(file));
    setError('');
  };

  const handleBgUpload = (file) => {
    if (!file) return;
    setBgFile(file);
    setBgPreview(filePreviewUrl(file));
    setPortalBackgroundKind('image');
    setError('');
  };

  const handleSave = async () => {
    if (!resolvedCompanyId) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const refDoc = doc(db, 'companies', resolvedCompanyId);
      await updateDoc(refDoc, {
        portalBrandingEnabled,
        brandColor,
        portalBackgroundKind: portalBrandingEnabled ? portalBackgroundKind : 'default',
        portalBackgroundSolidColor,
      });

      if (logoFile) {
        const storageRef = ref(storage, `companyLogos/${resolvedCompanyId}`);
        await uploadBytes(storageRef, logoFile, { contentType: logoFile.type || 'image/png' });
        const downloadURL = await getDownloadURL(storageRef);
        await updateDoc(refDoc, { logoUrl: downloadURL });
        setLogoPreview(downloadURL);
        setLogoFile(null);
      }

      if (portalBrandingEnabled && portalBackgroundKind === 'image' && bgFile) {
        const bgRef = ref(storage, `companyPortalBg/${resolvedCompanyId}`);
        await uploadBytes(bgRef, bgFile, { contentType: bgFile.type || 'image/png' });
        const bgUrl = await getDownloadURL(bgRef);
        await updateDoc(refDoc, { portalBackgroundImageUrl: bgUrl });
        setBgPreview(bgUrl);
        setBgFile(null);
      }

      await refresh?.();
      setSuccess('Workspace branding saved. Members will see updates on the dashboard.');
    } catch (e) {
      console.error('WorkspaceBrandingSettings save:', e);
      setError(e.message || 'Could not save branding settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <PaletteIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Workspace branding
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customize the member dashboard with your logo and background. Upload files or
              generate assets with AI, then save to apply.
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControlLabel
            control={(
              <Switch
                checked={portalBrandingEnabled}
                onChange={(e) => setPortalBrandingEnabled(e.target.checked)}
              />
            )}
            label="Enable custom member portal branding"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1 }}>
            When enabled, members see your logo in the sidebar, your brand color on primary
            actions, and the background style below instead of the default Shifty look.
          </Typography>

          <TextField
            label="Brand color"
            type="color"
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            sx={{ width: 140 }}
            size="small"
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Logo
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {LOGO_SPECS.label}. Use a preset for a transparent mark or describe your own.
            </Typography>

            {logoPreview && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo preview"
                  sx={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
                />
              </Box>
            )}

            <Stack spacing={1.5}>
              <Button component="label" variant="outlined" size="small" startIcon={<CloudUploadIcon />}>
                Upload logo
                <input
                  type="file"
                  hidden
                  accept={LOGO_ACCEPT}
                  onChange={(e) => handleLogoUpload(e.target.files?.[0] || null)}
                />
              </Button>
              {logoFile && <Chip size="small" label={logoFile.name} onDelete={() => setLogoFile(null)} />}

              <FormControl fullWidth size="small">
                <InputLabel id="logo-preset-label">AI preset</InputLabel>
                <Select
                  labelId="logo-preset-label"
                  label="AI preset"
                  value={logoPresetId}
                  onChange={(e) => setLogoPresetId(e.target.value)}
                >
                  {DEFAULT_LOGO_PROMPTS.map((preset) => (
                    <MenuItem key={preset.id} value={preset.id}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {DEFAULT_LOGO_PROMPTS.find((p) => p.id === logoPresetId)?.description}
              </Typography>

              <TextField
                label="Custom AI prompt (optional)"
                multiline
                minRows={2}
                size="small"
                value={logoPrompt}
                onChange={(e) => setLogoPrompt(e.target.value)}
                placeholder="Overrides the preset when filled in."
              />

              <Button
                variant="contained"
                startIcon={generatingLogo ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={() => generateAsset(BRANDING_ASSET_TYPES.LOGO)}
                disabled={generatingLogo || saving}
              >
                {generatingLogo ? 'Generating…' : 'Generate logo with AI'}
              </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Dashboard background
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {BACKGROUND_SPECS.label}. Upload a still image, GIF, or animated WebP for motion.
              AI generates still wallpapers optimized for cover display on mobile and desktop.
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="bg-kind-label">Background style</InputLabel>
              <Select
                labelId="bg-kind-label"
                label="Background style"
                value={portalBackgroundKind}
                onChange={(e) => setPortalBackgroundKind(e.target.value)}
                disabled={!portalBrandingEnabled}
              >
                <MenuItem value="default">Default (app background)</MenuItem>
                <MenuItem value="tint">Subtle brand tint</MenuItem>
                <MenuItem value="solid">Solid color</MenuItem>
                <MenuItem value="image">Image / animation</MenuItem>
              </Select>
            </FormControl>

            {portalBackgroundKind === 'solid' && (
              <TextField
                label="Solid background"
                type="color"
                value={portalBackgroundSolidColor}
                onChange={(e) => setPortalBackgroundSolidColor(e.target.value)}
                sx={{ width: 160, mb: 2 }}
                size="small"
              />
            )}

            {portalBackgroundKind === 'image' && bgPreview && (
              <Box
                sx={{
                  mb: 2,
                  height: 120,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  overflow: 'hidden',
                  ...shellSx,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}

            {portalBackgroundKind === 'image' && (
              <Stack spacing={1.5}>
                <Button component="label" variant="outlined" size="small" startIcon={<CloudUploadIcon />}>
                  Upload background
                  <input
                    type="file"
                    hidden
                    accept={BACKGROUND_ACCEPT}
                    onChange={(e) => handleBgUpload(e.target.files?.[0] || null)}
                  />
                </Button>
                {bgFile && (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={bgFile.name} onDelete={() => setBgFile(null)} />
                    {isAnimatedBackgroundMime(bgFile.type) && (
                      <Chip size="small" color="info" label="Animated" />
                    )}
                  </Stack>
                )}

                <FormControl fullWidth size="small">
                  <InputLabel id="bg-preset-label">AI preset</InputLabel>
                  <Select
                    labelId="bg-preset-label"
                    label="AI preset"
                    value={bgPresetId}
                    onChange={(e) => setBgPresetId(e.target.value)}
                  >
                    {DEFAULT_BACKGROUND_PROMPTS.map((preset) => (
                      <MenuItem key={preset.id} value={preset.id}>
                        {preset.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  {DEFAULT_BACKGROUND_PROMPTS.find((p) => p.id === bgPresetId)?.description}
                </Typography>

                <TextField
                  label="Custom AI prompt (optional)"
                  multiline
                  minRows={2}
                  size="small"
                  value={bgPrompt}
                  onChange={(e) => setBgPrompt(e.target.value)}
                  placeholder="Overrides the preset when filled in."
                />

                <Button
                  variant="contained"
                  startIcon={generatingBg ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                  onClick={() => generateAsset(BRANDING_ASSET_TYPES.BACKGROUND)}
                  disabled={generatingBg || saving}
                >
                  {generatingBg ? 'Generating…' : 'Generate background with AI'}
                </Button>
              </Stack>
            )}
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            minHeight: 140,
            position: 'relative',
            overflow: 'hidden',
            ...shellSx,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Live preview
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            {previewPortal.logoUrl ? (
              <Box
                component="img"
                src={previewPortal.logoUrl}
                alt="Preview logo"
                sx={{ maxHeight: 40, maxWidth: 160, objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No logo yet
              </Typography>
            )}
            <Chip
              size="small"
              label={portalBrandingEnabled ? 'Branding on' : 'Branding off'}
              color={portalBrandingEnabled ? 'primary' : 'default'}
            />
          </Stack>
        </Box>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || generatingLogo || generatingBg}
          >
            {saving ? 'Saving…' : 'Save branding'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
